import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, LogOut, Package, ClipboardCheck, FileBadge, Download, LayoutDashboard, AlertOctagon, CalendarClock, TrendingUp, CircleCheck, CircleX, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { ReturnToSuperAdminBanner } from "@/components/layout/AppLayout";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "assets", label: "My Equipment", icon: Package },
  { key: "inspections", label: "Inspections", icon: ClipboardCheck },
  { key: "certificates", label: "Certificates", icon: FileBadge },
];

export default function Portal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [assets, setAssets] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [certs, setCerts] = useState([]);
  const [dash, setDash] = useState(null);

  useEffect(() => {
    api.get("/portal/assets").then((r) => setAssets(r.data)).catch(() => {});
    api.get("/portal/inspections").then((r) => setInspections(r.data)).catch(() => {});
    api.get("/portal/certificates").then((r) => setCerts(r.data)).catch(() => {});
    api.get("/portal/dashboard").then((r) => setDash(r.data)).catch(() => {});
  }, []);

  const doLogout = async () => { await logout(); nav("/login"); };

  const download = async (id, num) => {
    const res = await api.get(`/certificates/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = `cert-${num}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const brand = user?.brand || {};
  const primary = brand.primary_color || "#01ADEF";

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="portal-page" style={{ "--brand-primary": primary }}>
      <ReturnToSuperAdminBanner user={user} />
      <header className="bg-white border-b border-gray-200" style={{ borderTop: `4px solid ${primary}` }}>
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {brand.logo_url ? (
              <img src={brand.logo_url} alt="" className="h-8 max-w-[140px] object-contain" />
            ) : (
              <div className="w-8 h-8 flex items-center justify-center" style={{ background: primary }}><ShieldCheck className="w-5 h-5 text-white" strokeWidth={2.5} /></div>
            )}
            <div>
              <div className="font-display text-lg font-bold tracking-tight">{brand.company || "Doc Q"}</div>
              <div className="text-xs text-gray-500 -mt-0.5 uppercase tracking-wider">Customer Portal</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-sm text-right">
              <div className="font-semibold">{user?.name}</div>
              <div className="text-xs text-gray-500 font-mono">{user?.email}</div>
            </div>
            <Button data-testid="portal-logout" onClick={doLogout} variant="outline" size="sm" className="rounded-full border-gray-300"><LogOut className="w-3.5 h-3.5 mr-1" />Logout</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6 md:p-8">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">Welcome</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Your compliance overview</h1>
        </div>

        <div className="flex items-center gap-1 mb-4" data-testid="portal-tabs">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button key={key} data-testid={`portal-tab-${key}`} onClick={() => setTab(key)}
              style={tab === key ? { background: primary, borderColor: primary, color: "#fff" } : {}}
              className={`inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider font-semibold border transition-colors ${tab === key ? "" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <PortalOverview dash={dash} primary={primary} />
        )}

        {tab === "assets" && (
          <div className="bg-white border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-gray-200 bg-gray-50"><Th>Asset</Th><Th>Tag</Th><Th>Category</Th><Th>Location</Th><Th>Status</Th></tr></thead>
              <tbody data-testid="portal-assets-table">
                {assets.length === 0 ? <tr><td colSpan={5} className="text-center py-10 text-gray-500">No assets assigned to your account yet.</td></tr>
                  : assets.map(a => (
                    <tr key={a.id} className="border-b border-gray-100 hover:bg-gray-50"><Td className="font-semibold">{a.name}</Td><Td className="font-mono">{a.asset_tag}</Td><Td className="capitalize">{a.category?.replace('_',' ')}</Td><Td>{a.location}</Td><Td><span className="text-xs font-bold px-2 py-1 uppercase tracking-wider border bg-[#D1FAE5] text-[#065F46] border-[#6EE7B7]">{a.status}</span></Td></tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "inspections" && (
          <div className="bg-white border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-gray-200 bg-gray-50"><Th>Date</Th><Th>Asset</Th><Th>Type</Th><Th>Status</Th><Th>Result</Th></tr></thead>
              <tbody data-testid="portal-inspections-table">
                {inspections.length === 0 ? <tr><td colSpan={5} className="text-center py-10 text-gray-500">No inspections yet.</td></tr>
                  : inspections.map(i => (
                    <tr key={i.id} className="border-b border-gray-100 hover:bg-gray-50"><Td className="font-mono">{i.scheduled_date?.slice(0,10)}</Td><Td><div className="font-semibold">{i.asset_name}</div><div className="text-xs text-gray-500 font-mono">{i.asset_tag}</div></Td><Td className="capitalize">{i.inspection_type?.replace('_',' ')}</Td><Td className="capitalize">{i.status?.replace('_',' ')}</Td><Td>{i.result ? <span className={`text-xs font-bold px-2 py-1 uppercase tracking-wider border ${i.result === 'pass' ? 'bg-[#D1FAE5] text-[#065F46] border-[#6EE7B7]' : 'bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]'}`}>{i.result}</span> : <span className="text-gray-400 text-xs">—</span>}</Td></tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "certificates" && (
          <div className="bg-white border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-gray-200 bg-gray-50"><Th>Cert #</Th><Th>Asset</Th><Th>Issued</Th><Th>Expires</Th><Th>Status</Th><Th></Th></tr></thead>
              <tbody data-testid="portal-certs-table">
                {certs.length === 0 ? <tr><td colSpan={6} className="text-center py-10 text-gray-500">No certificates yet.</td></tr>
                  : certs.map(c => (
                    <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <Td className="font-mono font-semibold">{c.certificate_number}</Td>
                      <Td><div className="font-semibold">{c.asset_name}</div><div className="font-mono text-xs text-gray-500">{c.asset_tag}</div></Td>
                      <Td className="font-mono">{c.issued_date}</Td>
                      <Td className="font-mono">{c.expiry_date}</Td>
                      <Td><span className={`text-xs font-bold px-2 py-1 uppercase tracking-wider border ${c.cert_status === 'valid' ? 'bg-[#D1FAE5] text-[#065F46] border-[#6EE7B7]' : c.cert_status === 'expiring_soon' ? 'bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]' : 'bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]'}`}>{c.cert_status === 'valid' ? `Valid · ${c.days_to_expiry}d` : c.cert_status === 'expiring_soon' ? `Expiring · ${c.days_to_expiry}d` : `Expired ${Math.abs(c.days_to_expiry)}d`}</span></Td>
                      <Td><button onClick={() => download(c.id, c.certificate_number)} data-testid={`portal-download-${c.id}`} className="text-[#01ADEF] hover:text-[#0198D6]"><Download className="w-4 h-4" /></button></Td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

function PortalOverview({ dash, primary }) {
  if (!dash) return <div className="text-sm text-gray-500 py-8">Loading dashboard…</div>;
  const k = dash.kpis;
  const chartData = dash.spend_last_12_months.map(m => ({ ...m, month: m.month.slice(2) }));
  return (
    <div data-testid="portal-overview">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <Kpi label="Assets" value={k.total_assets} icon={Package} accent="bg-[#01ADEF]" testid="portal-kpi-assets" />
        <Kpi label="Passed" value={k.passed_inspections} icon={CircleCheck} accent="bg-[#059669]" testid="portal-kpi-passed" />
        <Kpi label="Failed" value={k.failed_inspections} icon={CircleX} accent="bg-[#DC2626]" testid="portal-kpi-failed" />
        <Kpi label="Upcoming (30d)" value={k.upcoming_30d} icon={CalendarClock} accent="bg-[#0284C7]" testid="portal-kpi-upcoming" />
        <Kpi label="Past Due" value={k.past_due} icon={AlertOctagon} accent="bg-[#DC2626]" testid="portal-kpi-past-due" />
        <Kpi label="Valid Certs" value={k.approved_certs} icon={ShieldCheck} accent="bg-[#059669]" testid="portal-kpi-certs" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <SpendCard label="Total Spend" value={k.total_spend} icon={Wallet} accent="bg-[#01ADEF]" testid="portal-spend-total" />
        <SpendCard label="YTD Spend" value={k.ytd_spend} icon={TrendingUp} accent="bg-[#059669]" testid="portal-spend-ytd" />
        <SpendCard label="Charges" value={dash.recent_charges.length} suffix=" recent" icon={FileBadge} accent="bg-[#D97706]" testid="portal-spend-count" plain />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-gray-200 p-5">
          <div className="mb-3">
            <h2 className="font-display text-lg font-bold tracking-tight">Spend — Last 12 months</h2>
            <p className="text-xs text-gray-500 mt-0.5">Total charged per month across repairs, inspections, and certificates</p>
          </div>
          {chartData.every(m => m.amount === 0) ? (
            <div className="text-center py-16 text-sm text-gray-500">No spend recorded yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="month" tick={{ fill: "#6B7280", fontSize: 12 }} />
                <YAxis tick={{ fill: "#6B7280", fontSize: 12 }} />
                <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #D1D5DB" }} formatter={(v) => `$${v}`} />
                <Line type="monotone" dataKey="amount" stroke={primary} strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white border border-gray-200">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-2">
            <AlertOctagon className="w-4 h-4 text-[#DC2626]" />
            <h2 className="font-display text-lg font-bold tracking-tight">Past-Due Inspections</h2>
          </div>
          <div className="divide-y divide-gray-100" data-testid="past-due-list">
            {dash.past_due_list.length === 0 ? (
              <div className="px-5 py-10 text-center text-sm text-gray-500">Nothing past due — you&apos;re compliant.</div>
            ) : dash.past_due_list.map(i => (
              <div key={i.inspection_id} className="px-5 py-3 flex items-center justify-between" data-testid={`past-due-${i.inspection_id}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{i.asset_name}</div>
                  <div className="text-xs text-gray-500 font-mono">{i.asset_tag}</div>
                </div>
                <span className="text-xs font-bold px-2 py-1 uppercase tracking-wider border bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]">
                  {Math.abs(i.days)}d overdue
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {dash.recent_charges.length > 0 && (
        <div className="bg-white border border-gray-200 mt-4 overflow-x-auto">
          <div className="px-5 py-4 border-b border-gray-200"><h2 className="font-display text-lg font-bold tracking-tight">Recent Charges</h2></div>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-200 bg-gray-50"><Th>Date</Th><Th>Category</Th><Th>Description</Th><Th className="text-right">Amount</Th></tr></thead>
            <tbody data-testid="recent-charges-table">
              {dash.recent_charges.map(c => (
                <tr key={c.id} className="border-b border-gray-100">
                  <Td className="font-mono">{c.date}</Td>
                  <Td><span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border bg-gray-100 border-gray-300 text-gray-700">{c.category}</span></Td>
                  <Td className="text-gray-600">{c.description || "—"}</Td>
                  <Td className="font-mono text-right">${Number(c.amount || 0).toFixed(2)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const Th = ({ children, className = "" }) => <th className={`text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold text-gray-600 ${className}`}>{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
const Kpi = ({ label, value, icon: Icon, accent, testid }) => (
  <div className="bg-white border border-gray-200 p-4" data-testid={testid}>
    <div className="flex items-center justify-between mb-2"><span className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">{label}</span><div className={`w-6 h-6 ${accent} flex items-center justify-center`}><Icon className="w-3 h-3 text-white" strokeWidth={2.5} /></div></div>
    <div className="font-display text-2xl md:text-3xl font-bold tracking-tight">{value}</div>
  </div>
);
const SpendCard = ({ label, value, icon: Icon, accent, testid, plain, suffix }) => (
  <div className="bg-white border border-gray-200 p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-3"><span className="text-xs uppercase tracking-wider font-semibold text-gray-500">{label}</span><div className={`w-8 h-8 ${accent} flex items-center justify-center`}><Icon className="w-4 h-4 text-white" strokeWidth={2.5} /></div></div>
    <div className="font-display text-3xl md:text-4xl font-bold tracking-tight">{plain ? `${value}${suffix || ""}` : `$${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</div>
  </div>
);
